import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "../store";
import axiosInstance from "src/apis/axiosInstance";
import {
  APIResponseCover,
  Pagination,
  PartnerAPIResponseCover,
} from "./auth.d";
import { readAPIErrorMessage, throwErrApi } from "src/utils/errorUtil";
import { API_STATUS, DATA_LIMIT } from "src/utils/constants";

export interface CruiseLineDataForm {
  cruise_line_name: string;
  cruise_line_code: string;
  contact_number: string;
  contact_person_name: string;
  email_id: string;
}

export interface CruiseLineEditDataForm extends CruiseLineDataForm {
  id: string;
}

export interface CruiseLineData {
  id: string;
  cruise_line_name: string;
  cruise_line_code: string;
  contact_number: string;
  contact_person_name: string;
  email_id: string;
  status: number;
  created_date: string;
  updated_date: string;
}

export interface CruiseLinesState {
  data?: Pagination<CruiseLineData>;
  status: API_STATUS;
  list: {
    data: CruiseLineData[];
    status: API_STATUS;
  };
  view: {
    data?: CruiseLineData;
    status: API_STATUS;
  };
}

const initialState: CruiseLinesState = {
  data: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  list: {
    data: [],
    status: "idle",
  },
  view: {
    data: undefined,
    status: "idle",
  },
  status: "idle",
};

export const cruiseLinesSlice = createSlice({
  name: "cruiseLines",
  initialState,
  reducers: {
    refreshCruiseLineList: (state) => {
      state.status = "idle";
    },
    resetCruiseLineView(state) {
      state.view.status = "idle";
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchCruiseLines.pending, (state, action) => {
      state.status = "pending";
    });
    builder.addCase(fetchCruiseLines.fulfilled, (state, action) => {
      state.status = "success";
      state.data = action.payload;
    });
    builder.addCase(fetchCruiseLines.rejected, (state, action) => {
      state.status = "error";
    });
    builder.addCase(fetchListCruiseLines.pending, (state, action) => {
      state.list.status = "pending";
    });
    builder.addCase(fetchListCruiseLines.fulfilled, (state, action) => {
      state.list.status = "success";
      state.list.data = action.payload;
    });
    builder.addCase(fetchListCruiseLines.rejected, (state, action) => {
      state.list.status = "error";
    });
    builder.addCase(addCruiseLine.fulfilled, (state, action) => {
      state.status = "idle";
    });
    builder.addCase(viewCruiseLine.fulfilled, (state, action) => {
      state.view.data = action.payload;
      state.view.status = "success";
    });
    builder.addCase(viewCruiseLine.pending, (state, action) => {
      state.view.status = "pending";
    });
    builder.addCase(viewCruiseLine.rejected, (state, action) => {
      state.view.status = "error";
    });
  },
});

export const fetchCruiseLines = createAsyncThunk<
  Pagination<CruiseLineData>,
  { page: number; keyword: string },
  { state: RootState }
>("cruiseLine/fetch", async ({ page, keyword }, thunkAPI) => {
  try {
    const response = await axiosInstance.get<
      PartnerAPIResponseCover<Pagination<CruiseLineData>>,
      AxiosResponse<PartnerAPIResponseCover<Pagination<CruiseLineData>>>
    >(
      `partners/cruiseline?limit=${DATA_LIMIT}&keyword=${keyword}&offset=${
        DATA_LIMIT * (page - 1)
      }&format=json`
    );
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      throw new Error(readAPIErrorMessage(e));
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const fetchListCruiseLines = createAsyncThunk<
  CruiseLineData[],
  void,
  { state: RootState }
>("cruiseLines/fetchList", async (_, thunkAPI) => {
  try {
    const response = await axiosInstance.get<
      PartnerAPIResponseCover<CruiseLineData[]>,
      AxiosResponse<PartnerAPIResponseCover<CruiseLineData[]>>
    >(`partners/cruiseline/search/?format=json`);
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      throw new Error(readAPIErrorMessage(e));
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const deleteCruiseLine = createAsyncThunk<
  { status: string },
  string,
  { state: RootState }
>("cruiseLine/delete", async (id, thunkAPI) => {
  try {
    const response = await axiosInstance.delete<
      APIResponseCover<Record<never, never>>,
      AxiosResponse<APIResponseCover<Record<never, never>>>
    >(`partners/cruiseline/${id}?format=json`);
    return thunkAPI.fulfillWithValue({ status: "idle" });
  } catch (e) {
    if (e instanceof AxiosError) {
      throwErrApi(e);
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const updateCruiseLine = createAsyncThunk<
  { status: string },
  CruiseLineEditDataForm,
  { state: RootState }
>("cruiseLine/update", async (data: CruiseLineEditDataForm, thunkAPI) => {
  try {
    const response = await axiosInstance.put<
      APIResponseCover<Record<never, never>>,
      AxiosResponse<APIResponseCover<Record<never, never>>, CruiseLineEditDataForm>,
      CruiseLineEditDataForm
    >(`partners/cruiseline/${data.id}?format=json`, {
      ...data,
    });
    return thunkAPI.fulfillWithValue({ status: "pending" });
  } catch (e) {
    if (e instanceof AxiosError) {
      throwErrApi(e);
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const viewCruiseLine = createAsyncThunk<
  CruiseLineData,
  string,
  { state: RootState }
>("cruiseLine/view", async (id, thunkAPI) => {
  try {
    const response = await axiosInstance.get<
      APIResponseCover<CruiseLineData>,
      AxiosResponse<APIResponseCover<CruiseLineData>>
    >(`partners/cruiseline/${id}?format=json`);
    return response.data.data;
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(readAPIErrorMessage(e));
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const addCruiseLine = createAsyncThunk<
  { status: string },
  CruiseLineDataForm,
  { state: RootState }
>("cruiseLine/add", async (data: CruiseLineDataForm, thunkAPI) => {
  try {
    const response = await axiosInstance.post<
      APIResponseCover<Record<never, never>>,
      AxiosResponse<APIResponseCover<Record<never, never>>, CruiseLineDataForm>,
      CruiseLineDataForm
    >("partners/cruiseline?format=json", data);
    return thunkAPI.fulfillWithValue({ status: "idle" });
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      throwErrApi(e);
    } else if (e instanceof Error) {
      throw new Error(e.message);
    }
    return thunkAPI.rejectWithValue(false);
  }
});

export const { refreshCruiseLineList, resetCruiseLineView } = cruiseLinesSlice.actions;

export default cruiseLinesSlice.reducer;
